import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/site/SectionHeading";
import {
  INSIGHTS,
  INSIGHT_CATEGORIES,
  formatInsightDate,
} from "@/lib/insights";
import { cn } from "@/lib/utils";
import { PageBanner } from "@/components/site/PageBanner";
import { SafeImage } from "@/components/site/SafeImage";
import { useSeo } from "@/hooks/useSeo";
import { useJsonLd } from "@/hooks/useJsonLd";
import { blogListingLd } from "@/lib/structured-data";

export function InsightsPage() {
  const [activeCategory, setActiveCategory] = useState<
    (typeof INSIGHT_CATEGORIES)[number]
  >("All");

  useSeo({
    title:
      "Dubai Property Insights, Market Reports & Investor Guides | Hive Homes",
    description:
      "Dubai property market reports, neighbourhood guides, and investor briefings from Hive Homes. Stay informed and make confident property decisions in Dubai.",
  });

  useJsonLd("insights-blog", blogListingLd(INSIGHTS));

  const articles = useMemo(() => {
    const sorted = [...INSIGHTS].sort((a, b) => (a.date < b.date ? 1 : -1));
    if (activeCategory === "All") return sorted;
    return sorted.filter((a) => a.category === activeCategory);
  }, [activeCategory]);

  return (
    <>
      {/* Hero — client banner photo behind the page heading */}
      <PageBanner
        image="/images/site/insights.jpg"
        alt="Market reports on a desk in front of a window over the Dubai skyline"
      >
        <SectionHeading
          as="h1"
          eyebrow="Insights"
          size="xl"
          title="Dubai Property. Knowledge You Can Use."
          body="Market reports, neighbourhood guides, investor briefings, and regulatory updates — everything you need to make confident property decisions in Dubai."
        />
      </PageBanner>

      {/* Category filter — its own strip below the banner so every page
          banner keeps the same fixed dimensions. */}
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]/60">
        <div className="container-wide py-5 md:py-6">
          <nav
            aria-label="Filter articles by category"
            className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0 sm:overflow-visible"
          >
            <ul className="flex w-max gap-2 sm:w-auto sm:flex-wrap">
              {INSIGHT_CATEGORIES.map((cat) => (
                <li key={cat}>
                  <button
                    type="button"
                    onClick={() => setActiveCategory(cat)}
                    className={cn(
                      "inline-flex h-11 items-center px-4 text-[11.5px] uppercase tracking-[0.16em] transition-colors lg:h-9",
                      activeCategory === cat
                        ? "bg-[var(--navy)] text-[var(--cream)]"
                        : "border border-[var(--line-strong)] text-[var(--text-soft)] hover:border-[var(--gold-bright)] hover:text-[var(--gold-bright)]",
                    )}
                  >
                    {cat}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      <section className="container-wide py-12 md:py-20 short:py-10">
        {articles.length === 0 ? (
          <div className="border border-dashed border-[var(--line-strong)] p-12 text-center">
            <p className="font-display text-[22px] text-[var(--text)]">
              Nothing here yet.
            </p>
            <p className="mt-3 text-[14px] text-[var(--text-soft)]">
              We're working on more articles in this category — check back soon.
            </p>
          </div>
        ) : (
          <ul className="grid gap-8 sm:gap-10 md:grid-cols-2 md:gap-x-10 md:gap-y-14 lg:grid-cols-3">
            {articles.map((a) => (
              <li key={a.slug} className="group flex flex-col">
                <Link
                  to={`/insights/${a.slug}`}
                  className="flex h-full flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[var(--surface-2)]">
                    <SafeImage
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-4 pt-5">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold-bright)]">
                      {a.category}
                    </p>
                    <h2
                      className="font-display text-[22px] leading-tight text-[var(--text)] transition-colors group-hover:text-[var(--gold-bright)] sm:text-[26px]"
                      style={{ fontVariationSettings: '"opsz" 36' }}
                    >
                      {a.title}
                    </h2>
                    <p className="text-[14.5px] leading-[1.65] text-[var(--text-soft)]">
                      {a.excerpt}
                    </p>
                    <div className="mt-auto pt-3 text-[12px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
                      {formatInsightDate(a.date)} · {a.readMinutes} min read
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
